import gql from 'graphql-tag'
import { FetchResult, useMutation } from '@apollo/client'
import { client } from 'braintree-web'
import { useCheckout } from '../../../modules/checkout/hooks/useCheckout'

export const useBraintree = () => {
  const { lockCheckout, unlockCheckout } = useCheckout()
  const [createBraintreeToken] = useMutation<CreateBraintreeTokenResponse>(createBraintreeClientTokenMutation)

  return {
    authorizeCard: createAuthorizeCard({
      lockCheckout,
      unlockCheckout,
      createBraintreeToken
    })
  }
}

export const createBraintreeClientTokenMutation = gql`
  mutation {
    createBraintreeClientToken
  }
`

type CreateBraintreeToken = () => Promise<FetchResult<CreateBraintreeTokenResponse>>

interface CreateBraintreeTokenResponse {
  createBraintreeClientToken: string
}

interface BraintreeClient {
  request: (deps: any) => Promise<any>
}

interface AuthorizeCard {
  creditCardData: {
    number: string
    expMonth: string
    expYear: string
    cvv: string
  }
  billingAddressData: {
    postalCode: string
    streetAddress: string
    countryCodeAlpha2: string
  }
}

interface CreateBraintreeClient {
  createBraintreeToken: CreateBraintreeToken
  lockCheckout: () => void
  unlockCheckout: () => void
}

const createBraintreeClient = async (createBraintreeToken: CreateBraintreeToken) => new Promise<BraintreeClient>((resolve, reject) => {
  createBraintreeToken().then(payload => {
    const token = payload.data?.createBraintreeClientToken
    if (token) {
      client.create({ authorization: token }, (createErr, clientInstance) => {
        const client: BraintreeClient = clientInstance
        resolve(client)
      })
    } else {
      reject(new Error('Whoops, something went wrong! If the problem persists, please contact us.'))
    }
  }).catch(e => {
    reject(e)
  })
})

const createAuthorizeCard = (deps: CreateBraintreeClient) => async (params: AuthorizeCard) => {
  const {
    number,
    expMonth,
    expYear,
    cvv
  } = params.creditCardData

  const {
    countryCodeAlpha2,
    postalCode,
    streetAddress
  } = params.billingAddressData

  const data = {
    creditCard: {
      number,
      cvv,
      expirationDate: `${expMonth}/${expYear}`,
      billingAddress: {
        postalCode,
        streetAddress,
        countryCodeAlpha2
      }
    }
  }

  deps.lockCheckout()

  try {
    const braintreeClient = await createBraintreeClient(deps.createBraintreeToken)
    const response = await braintreeClient.request({
      endpoint: 'payment_methods/credit_cards',
      method: 'post',
      data
    })
    const [creditCard] = response.creditCards
    deps.unlockCheckout()
    return creditCard.nonce
  } catch (e) {
    deps.unlockCheckout()
    throw e
  }
}
